import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";
import { handleApiError } from "../../pages/UBTAdmin/ProjectDetails/ProjectHelper/ViewProjectHandleApiError";

// Types
export interface TeamProject {
  perfId: number;
  projectName: string;
}

export interface ProjectDetails {
  perfId: number;
  perfDateLastUpd: string;
  perfStartDate: string;
  perfEndDate: string;
  perfPspGoal: string;
  perfStatus: string;
  perfProblem: string;
  perfGoal: string;
}

export interface ProjectDocument {
  projectId: number;
  projectName: string;
  documentId: number;
  documentName: string;
  savedBy: string;
  savedByTeamRole: string;
  dateSaved: string;
}

export interface TeamLevelDocument {
  documentId: number;
  documentName: string;
  savedBy: string;
  savedByTeamRole: string;
  dateSaved: string;
}

export interface TeamDetail {
  teamId: number;
  teamName: string;
  teamDateCreated: string;
  serviceDeliveryType: string;
}

export interface TeamAggregatedData {
  teamDetails: TeamDetail[];
}

export interface TeamDocumentState {
  // Team data
  teamData: TeamDetail | null;
  teamDataLoading: boolean;
  teamDataError: string | null;

  // Projects list for dropdown
  projects: TeamProject[];
  projectsLoading: boolean;
  projectsError: string | null;

  // Selected project details
  selectedProjectDetails: ProjectDetails | null;
  selectedProjectDetailsLoading: boolean;
  selectedProjectDetailsError: string | null;

  // Selected project documents
  selectedProjectDocuments: ProjectDocument[];
  selectedProjectDocumentsLoading: boolean;
  selectedProjectDocumentsError: string | null;

  // All project documents
  allProjectDocuments: ProjectDocument[];
  allProjectDocumentsLoading: boolean;
  allProjectDocumentsError: string | null;

  // Team level documents
  teamLevelDocuments: TeamLevelDocument[];
  teamLevelDocumentsLoading: boolean;
  teamLevelDocumentsError: string | null;

  // Download state
  downloadLoading: boolean;
  downloadError: string | null;

  // Upload state
  uploadLoading: boolean;
  uploadError: string | null;
  uploadSuccess: boolean;
  uploadMessage: string | null;

  // Delete state
  deleteLoading: boolean;
  deleteError: string | null;
  deleteSuccess: boolean;
  deleteMessage: string | null;

  // Overall page loading (true when any initial data is loading)
  pageLoading: boolean;
}

const initialState: TeamDocumentState = {
  teamData: null,
  teamDataLoading: false,
  teamDataError: null,

  projects: [],
  projectsLoading: false,
  projectsError: null,

  selectedProjectDetails: null,
  selectedProjectDetailsLoading: false,
  selectedProjectDetailsError: null,

  selectedProjectDocuments: [],
  selectedProjectDocumentsLoading: false,
  selectedProjectDocumentsError: null,

  allProjectDocuments: [],
  allProjectDocumentsLoading: false,
  allProjectDocumentsError: null,

  teamLevelDocuments: [],
  teamLevelDocumentsLoading: false,
  teamLevelDocumentsError: null,

  downloadLoading: false,
  downloadError: null,

  uploadLoading: false,
  uploadError: null,
  uploadSuccess: false,
  uploadMessage: null,

  deleteLoading: false,
  deleteError: null,
  deleteSuccess: false,
  deleteMessage: null,

  pageLoading: false,
};

// Async Thunks

// 0. Fetch team data (team name, etc.)
export const fetchTeamData = createAsyncThunk<
  TeamDetail | null,
  { teamId: number | string; nuid: string | null },
  { rejectValue: string }
>(
  "teamDocuments/fetchTeamData",
  async ({ teamId, nuid }, { rejectWithValue, signal }) => {
    try {
      const options: any = {
        signal,
        headers: {
          accept: "application/json",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      };

      const url = endpoints.ubt.members.getTeamMembers(teamId);
      const response = await api.get<TeamAggregatedData>(url, options);

      // Return first item from array (all items have same team info)
      if (response.data?.teamDetails?.length > 0) {
        return response.data.teamDetails[0];
      }
      return null;
    } catch (error: any) {
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to fetch team data",
      );
    }
  },
);

// 1. Fetch team projects for dropdown
export const fetchTeamProjects = createAsyncThunk(
  "teamDocuments/fetchTeamProjects",
  async (
    { teamId, nuid }: { teamId: number | string; nuid: string | null },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        headers: {
          accept: "application/json",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      };

      const url = endpoints.ubt.teamDocuments.getTeamProjects(teamId);

      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to fetch team projects",
      );
    }
  },
);

// 2. Fetch project details
export const fetchProjectDetails = createAsyncThunk(
  "teamDocuments/fetchProjectDetails",
  async (
    { projectId, nuid }: { projectId: number | string; nuid: string | null },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        headers: {
          accept: "application/json",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      };

      const url = endpoints.ubt.teamDocuments.getProjectDetails(projectId);
      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to fetch project details",
      );
    }
  },
);

// 3. Fetch selected project documents
export const fetchProjectDocuments = createAsyncThunk(
  "teamDocuments/fetchProjectDocuments",
  async (
    {
      teamId,
      projectId,
      nuid,
    }: {
      teamId: number | string;
      projectId: number | string;
      nuid: string | null;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        headers: {
          accept: "application/json",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      };

      const url = endpoints.ubt.teamDocuments.getProjectDocuments(
        teamId,
        projectId,
      );
      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to fetch project documents",
      );
    }
  },
);

// 4. Fetch all project documents
export const fetchAllProjectDocuments = createAsyncThunk(
  "teamDocuments/fetchAllProjectDocuments",
  async (
    { teamId, nuid }: { teamId: number | string; nuid: string | null },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        headers: {
          accept: "application/json",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      };

      const url = endpoints.ubt.teamDocuments.getAllProjectDocuments(teamId);
      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to fetch all project documents",
      );
    }
  },
);

// 5. Fetch team level documents
export const fetchTeamLevelDocuments = createAsyncThunk(
  "teamDocuments/fetchTeamLevelDocuments",
  async (
    { teamId, nuid }: { teamId: number | string; nuid: string | null },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        headers: {
          accept: "application/json",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      };

      const url = endpoints.ubt.teamDocuments.getTeamLevelDocuments(teamId);
      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to fetch team level documents",
      );
    }
  },
);

// 6. Download document
export const downloadDocument = createAsyncThunk(
  "teamDocuments/downloadDocument",
  async (
    {
      type,
      typeId,
      documentId,
      nuid,
    }: {
      type: "team" | "project";
      typeId: number | string;
      documentId: number | string;
      nuid: string | null;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        responseType: "blob",
        headers: {
          accept: "application/octet-stream",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      };

      const url = endpoints.ubt.teamDocuments.downloadDocument(
        type,
        typeId,
        documentId,
      );
      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to download document",
      );
    }
  },
);

// 7. Upload document
export const uploadDocument = createAsyncThunk(
  "teamDocuments/uploadDocument",
  async (
    {
      type,
      typeId,
      file,
      createdBy,
      createdByRole,
      nuid,
    }: {
      type: "team" | "project";
      typeId: number | string;
      file: File;
      createdBy: string;
      createdByRole: string;
      nuid: string | null;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("createdBy", createdBy);
      formData.append("createdByRole", createdByRole);

      const options: any = {
        signal,
        headers: {
          "Content-Type": "multipart/form-data",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      };

      const url = endpoints.ubt.teamDocuments.uploadDocument(type, typeId);
      const response = await api.post(url, formData, options);
      return response.data;
    } catch (error: any) {
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to upload document",
      );
    }
  },
);

// 8. Delete document
export const deleteDocument = createAsyncThunk(
  "teamDocuments/deleteDocument",
  async (
    {
      documentId,
      nuid,
    }: {
      documentId: number | string;
      nuid: string | null;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        headers: {
          accept: "application/json",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      };

      const url = endpoints.ubt.teamDocuments.deleteDocument(documentId);
      const response = await api.delete(url, options);
      return response.data;
    } catch (error: any) {
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to delete document",
      );
    }
  },
);

// Slice
const teamDocumentSlice = createSlice({
  name: "teamDocuments",
  initialState,
  reducers: {
    resetTeamDocuments: (state) => {
      Object.assign(state, initialState);
    },
    resetSelectedProjectDocuments: (state) => {
      state.selectedProjectDocuments = [];
      state.selectedProjectDocumentsLoading = false;
      state.selectedProjectDocumentsError = null;
    },
    clearDownloadError: (state) => {
      state.downloadError = null;
    },
    clearUploadState: (state) => {
      state.uploadError = null;
      state.uploadSuccess = false;
      state.uploadMessage = null;
    },
    clearDeleteState: (state) => {
      state.deleteError = null;
      state.deleteSuccess = false;
      state.deleteMessage = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Team Data
    builder
      .addCase(fetchTeamData.pending, (state) => {
        state.teamDataLoading = true;
        state.teamDataError = null;
      })
      .addCase(fetchTeamData.fulfilled, (state, action) => {
        state.teamDataLoading = false;
        state.teamData = action.payload;
      })
      .addCase(fetchTeamData.rejected, (state, action) => {
        state.teamDataLoading = false;
        state.teamDataError = action.payload as string;
        state.teamData = null;
      });

    // Fetch Team Projects
    builder
      .addCase(fetchTeamProjects.pending, (state) => {
        state.projectsLoading = true;
        state.projectsError = null;
        state.pageLoading = true;
      })
      .addCase(fetchTeamProjects.fulfilled, (state, action) => {
        state.projectsLoading = false;
        state.projects = action.payload?.items ?? [];
        state.pageLoading = false;
      })
      .addCase(fetchTeamProjects.rejected, (state, action) => {
        state.projectsLoading = false;
        state.projectsError = action.payload as string;
        state.pageLoading = false;
      });

    // Fetch Project Details
    builder
      .addCase(fetchProjectDetails.pending, (state) => {
        state.selectedProjectDetailsLoading = true;
        state.selectedProjectDetailsError = null;
      })
      .addCase(fetchProjectDetails.fulfilled, (state, action) => {
        state.selectedProjectDetailsLoading = false;
        state.selectedProjectDetails = action.payload;
      })
      .addCase(fetchProjectDetails.rejected, (state, action) => {
        state.selectedProjectDetailsLoading = false;
        state.selectedProjectDetailsError = action.payload as string;
        state.selectedProjectDetails = null;
      });

    // Fetch Project Documents
    builder
      .addCase(fetchProjectDocuments.pending, (state) => {
        state.selectedProjectDocumentsLoading = true;
        state.selectedProjectDocumentsError = null;
      })
      .addCase(fetchProjectDocuments.fulfilled, (state, action) => {
        state.selectedProjectDocumentsLoading = false;
        state.selectedProjectDocuments = action.payload?.items ?? [];
      })
      .addCase(fetchProjectDocuments.rejected, (state, action) => {
        state.selectedProjectDocumentsLoading = false;
        state.selectedProjectDocumentsError = action.payload as string;
        // Clear documents when fetch fails (e.g., "No documents found")
        state.selectedProjectDocuments = [];
      });

    // Fetch All Project Documents
    builder
      .addCase(fetchAllProjectDocuments.pending, (state) => {
        state.allProjectDocumentsLoading = true;
        state.allProjectDocumentsError = null;
        state.pageLoading = true;
      })
      .addCase(fetchAllProjectDocuments.fulfilled, (state, action) => {
        state.allProjectDocumentsLoading = false;
        state.allProjectDocuments = action.payload?.items ?? [];
        state.pageLoading = false;
      })
      .addCase(fetchAllProjectDocuments.rejected, (state, action) => {
        state.allProjectDocumentsLoading = false;
        state.allProjectDocumentsError = action.payload as string;
        state.pageLoading = false;
        // Clear documents when fetch fails
        state.allProjectDocuments = [];
      });

    // Fetch Team Level Documents
    builder
      .addCase(fetchTeamLevelDocuments.pending, (state) => {
        state.teamLevelDocumentsLoading = true;
        state.teamLevelDocumentsError = null;
        state.pageLoading = true;
      })
      .addCase(fetchTeamLevelDocuments.fulfilled, (state, action) => {
        state.teamLevelDocumentsLoading = false;
        state.teamLevelDocuments = action.payload?.items ?? [];
        state.pageLoading = false;
      })
      .addCase(fetchTeamLevelDocuments.rejected, (state, action) => {
        state.teamLevelDocumentsLoading = false;
        state.teamLevelDocumentsError = action.payload as string;
        state.pageLoading = false;
        // Clear documents when fetch fails
        state.teamLevelDocuments = [];
      });

    // Download Document
    builder
      .addCase(downloadDocument.pending, (state) => {
        state.downloadLoading = true;
        state.downloadError = null;
      })
      .addCase(downloadDocument.fulfilled, (state) => {
        state.downloadLoading = false;
      })
      .addCase(downloadDocument.rejected, (state, action) => {
        state.downloadLoading = false;
        state.downloadError = action.payload as string;
      });

    // Upload Document
    builder
      .addCase(uploadDocument.pending, (state) => {
        state.uploadLoading = true;
        state.uploadError = null;
        state.uploadSuccess = false;
        state.uploadMessage = null;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.uploadLoading = false;
        state.uploadSuccess = true;
        state.uploadMessage =
          action.payload?.message ?? "Document uploaded successfully";
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.uploadLoading = false;
        state.uploadSuccess = false;
        state.uploadError = action.payload as string;
      });

    // Delete Document
    builder
      .addCase(deleteDocument.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
        state.deleteSuccess = false;
        state.deleteMessage = null;
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccess = true;
        state.deleteMessage =
          action.payload?.message ?? "Document deleted successfully";
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccess = false;
        state.deleteError = action.payload as string;
      });
  },
});

export const {
  resetTeamDocuments,
  resetSelectedProjectDocuments,
  clearDownloadError,
  clearUploadState,
  clearDeleteState,
} = teamDocumentSlice.actions;

export default teamDocumentSlice.reducer;
